import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { doc, setDoc, getDoc, collection, addDoc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  uploadImage,
  deleteImage,
  isBase64DataUrl,
  isFirebaseStorageUrl,
  validateImageFile,
} from "../../utils/imageUpload";

const EventDataContext = createContext(null);

export function useEventData() {
  const context = useContext(EventDataContext);
  if (!context) {
    throw new Error("useEventData must be used within EventDataProvider");
  }
  return context;
}

// Initial data structure for the entire event
const initialEventData = {
  // Information page data
  information: {
    eventName: { value: "", required: true },
    eventDate: { value: null, required: true },
    eventType: { value: "", required: true },
    eventStatus: { value: "", required: true },
    description: { value: "", required: false },
    locationName: { value: "", required: true },
    googleMapsLink: { value: "", required: true },
    startTime: { value: "", required: true },
    endTime: { value: "", required: true },
  },

  // Theme page data
  theme: {
    mainColor: { value: "#7B3F00", required: false },
    secondaryColor: { value: "#4B5B63", required: false },
    backgroundColor: { value: "#A34BFF", required: false },
    fontColor: { value: "#4B5B63", required: false },
    backgroundImage: { value: null, required: false },
    selectedFont: { value: "maaja", required: false },
    headerFontSize: { value: 32, required: false },
    detailFontSize: { value: 16, required: false },
    buttonFontSize: { value: 18, required: false },
    borderStyle: { value: "fancy", required: false },
  },

  // Main page data
  main: {
    backgroundImage: { value: null, required: false },
    eventTitle: { value: "", required: true },
    eventDescription: { value: "", required: true },
    textPosition: { value: "center", required: false },
    descriptionAlignment: { value: "center", required: false },
    wishButton: {
      enabled: { value: false, required: false },
      icon: { value: "star", required: false },
      text: { value: "", required: false }, // removed persistRaw so only string stored
    },
  },

  // Gift page data
  gift: {
    backgroundImage: { value: null, required: false },
    giftButton: {
      enabled: { value: false, required: false },
      icon: { value: "star", required: false },
      text: { value: "", required: false },
    },
    giftReceiverSetup: {
      groomBrideToggle: { value: true, required: false },
    },
    formSettings: {
      nameField: {
        enabled: { value: true, required: false },
        placeholder: { value: "Tell us your name!", required: false },
      },
      uploadSlip: { value: true, required: false },
      allowAnonymous: {
        enabled: { value: true, required: false },
        text: { value: "Anonymous", required: false },
      },
    },
    confirmButton: {
      enabled: { value: true, required: false },
      icon: { value: "star", required: false },
      text: { value: "Confirm", required: false },
    },
    accounts: {
      accountName: { value: "", required: false },
      accountNumber: { value: "", required: false },
      accountBank: { value: "", required: false },
    }
  },

  // Wish page data
  wish: {
    backgroundImage: { value: null, required: false },
    senderNameField: {
      enabled: { value: true, required: false },
      placeholder: { value: "Tell us your name!", required: false },
      characterLimit: { value: 50, required: false },
    },
    wishField: {
      enabled: { value: true, required: false },
      placeholder: { value: "Write your wish...", required: false },
      characterLimit: { value: 200, required: false },
    },
    confirmButton: {
      enabled: { value: true, required: false },
      icon: { value: "star", required: false },
      text: { value: "Confirm", required: false },
    },
    randomWish: {
      enabled: { value: false, required: false },
      texts: { value: [], required: false },
    },
    previewButton: {
      enabled: { value: false, required: false },
      icon: { value: "star", required: false },
      text: { value: "Preview", required: false },
    },
  },

  // Album page data
  album: {
    backgroundImage: { value: null, required: false },
    layout: {
      sortingType: { value: "time", required: false },
      pictureFrameSize: { value: "S", required: false },
      numberOfColumns: { value: "1", required: false },
    },
    likeVote: {
      enabled: { value: true, required: false },
      showNumberOfLikes: { value: true, required: false },
    },
    wishTextDisplay: {
      showWisherName: { value: true, required: false },
      showViewWishes: { value: true, required: false },
    },
    filterOptions: {
      enabled: { value: false, required: false },
      showAll: { value: true, required: false },
      showPhotosOnly: { value: false, required: false },
      showWishesOnly: { value: false, required: false },
    },
  },
};

export function EventDataProvider({ children, eventId: initialEventId }) {
  const [eventData, setEventData] = useState(initialEventData);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [currentEventId, setCurrentEventId] = useState(initialEventId || null);
  const [isLoading, setIsLoading] = useState(false);

  const validateRequiredFields = useCallback(() => {
    const errors = {};

    const checkSection = (section, sectionName) => {
      Object.entries(section).forEach(([key, field]) => {
        if (field && typeof field === "object") {
          if (
            field.hasOwnProperty("value") &&
            field.hasOwnProperty("required")
          ) {
            // Basic required check
            if (field.required && (!field.value || field.value === "")) {
              if (!errors[sectionName]) errors[sectionName] = {};
              errors[sectionName][key] = "This field is required";
            }
            // Custom numeric check for characterLimit
            if (key === "characterLimit") {
              const numeric = Number(field.value);
              if (
                field.value !== "" &&
                field.value !== null &&
                (Number.isNaN(numeric) || !Number.isFinite(numeric) || numeric < 0)
              ) {
                if (!errors[sectionName]) errors[sectionName] = {};
                errors[sectionName][key] = "Must be a non-negative number";
              }
            }
          } else {
            // Handle nested objects
            const nestedErrors = {};
            Object.entries(field).forEach(([nestedKey, nestedField]) => {
              if (
                nestedField &&
                typeof nestedField === "object" &&
                nestedField.hasOwnProperty("value") &&
                nestedField.hasOwnProperty("required")
              ) {
                if (
                  nestedField.required &&
                  (!nestedField.value || nestedField.value === "")
                ) {
                  nestedErrors[nestedKey] = "This field is required";
                }
                if (nestedKey === "characterLimit") {
                  const numeric = Number(nestedField.value);
                  if (
                    nestedField.value !== "" &&
                    nestedField.value !== null &&
                    (Number.isNaN(numeric) || !Number.isFinite(numeric) || numeric < 0)
                  ) {
                    nestedErrors[nestedKey] = "Must be a non-negative number";
                  }
                }
              }
            });
            if (Object.keys(nestedErrors).length > 0) {
              if (!errors[sectionName]) errors[sectionName] = {};
              errors[sectionName][key] = nestedErrors;
            }
          }
        }
      });
    };

    Object.entries(eventData).forEach(([sectionName, section]) => {
      checkSection(section, sectionName);
    });

    // --- Conditional Validation Logic ---

    // Helper to add error
    const addError = (section, field, subField, message = "This field is required") => {
      if (!errors[section]) errors[section] = {};
      if (subField) {
        if (!errors[section][field]) errors[section][field] = {};
        errors[section][field][subField] = message;
      } else {
        errors[section][field] = message;
      }
    };

    // Main Page: Wish Button
    if (eventData.main?.wishButton?.enabled?.value) {
      if (!eventData.main.wishButton.text?.value) {
        addError("main", "wishButton", "text");
      }
    }

    // Gift Page: Gift Button
    if (eventData.gift?.giftButton?.enabled?.value) {
      if (!eventData.gift.giftButton.text?.value) {
        addError("gift", "giftButton", "text");
      }
      // Bank Account Details (Required if Gift Button is enabled)
      if (!eventData.gift.accounts?.accountName?.value) {
        addError("gift", "accounts", "accountName");
      }
      if (!eventData.gift.accounts?.accountNumber?.value) {
        addError("gift", "accounts", "accountNumber");
      }
      if (!eventData.gift.accounts?.accountBank?.value) {
        addError("gift", "accounts", "accountBank");
      }
    }

    // Gift Page: Confirm Button
    if (eventData.gift?.confirmButton?.enabled?.value) {
      if (!eventData.gift.confirmButton.text?.value) {
        addError("gift", "confirmButton", "text");
      }
    }

    // Gift Page: Form Settings - Name Field
    if (eventData.gift?.formSettings?.nameField?.enabled?.value) {
      if (!eventData.gift.formSettings.nameField.placeholder?.value) {
        addError("gift", "formSettings", "nameField"); // Assuming nameField is the object containing placeholder, but wait, structure is nameField: { enabled: ..., placeholder: ... }
        // The generic loop handles nested objects, but here we need to be specific about the path for the error object structure
        // The error structure expected by the UI is errors[section][key][nestedKey]
        // So for nameField.placeholder, it should be errors.gift.formSettings.nameField.placeholder? No, wait.
        // Let's look at the structure: gift -> formSettings -> nameField -> placeholder
        // The generic loop does: errors[sectionName][key] = nestedErrors.
        // So errors.gift.formSettings = { nameField: { placeholder: "..." } } ?
        // Actually, formSettings is the key. nameField is the nestedKey. placeholder is... wait.
        // formSettings structure: { nameField: { enabled:..., placeholder:... }, ... }
        // This is 3 levels deep! The generic loop only handles 2 levels (Section -> Field -> NestedField).
        // formSettings is Section -> Field (formSettings) -> NestedField (nameField) -> ... wait, nameField is an object with value/required?
        // No, nameField has { enabled: {value...}, placeholder: {value...} }.
        // The generic loop:
        // Level 1: section (gift)
        // Level 2: key (formSettings) -> field (object)
        // Level 3: nestedKey (nameField) -> nestedField (object with enabled, placeholder)
        // The generic loop checks if nestedField has value/required.
        // nameField DOES NOT have value/required directly. It has sub-properties.
        // So the generic loop SKIPS nameField!
        // We need to handle this deep nesting or fix the generic loop.
        // But for now, let's implement the specific checks here.

        // Correct error path for deep nesting needs to match what the UI expects.
        // If the UI expects errors.gift.formSettings.nameField.placeholder, we need to build that.
        // But let's check how the UI consumes it.
        // gift.js: hasNestedError('formSettings', 'nameField')?.placeholder
        // This implies: validationErrors.gift.formSettings.nameField.placeholder

        if (!errors.gift) errors.gift = {};
        if (!errors.gift.formSettings) errors.gift.formSettings = {};
        // We need to ensure formSettings is an object we can attach to.
        // But wait, if the generic loop found errors in other parts of formSettings, it might have created it.
        // Let's be safe.

        // Re-implementing addError for deep nesting
        if (!errors.gift.formSettings.nameField) errors.gift.formSettings.nameField = {};
        errors.gift.formSettings.nameField.placeholder = "This field is required";
      }
    }

    // Gift Page: Form Settings - Allow Anonymous
    if (eventData.gift?.formSettings?.allowAnonymous?.enabled?.value) {
      if (!eventData.gift.formSettings.allowAnonymous.text?.value) {
        if (!errors.gift) errors.gift = {};
        if (!errors.gift.formSettings) errors.gift.formSettings = {};
        if (!errors.gift.formSettings.allowAnonymous) errors.gift.formSettings.allowAnonymous = {};
        errors.gift.formSettings.allowAnonymous.text = "This field is required";
      }
    }

    // Wish Page: Sender Name Field
    if (eventData.wish?.senderNameField?.enabled?.value) {
      if (!eventData.wish.senderNameField.placeholder?.value) {
        addError("wish", "senderNameField", "placeholder");
      }
    }

    // Wish Page: Wish Field
    if (eventData.wish?.wishField?.enabled?.value) {
      if (!eventData.wish.wishField.placeholder?.value) {
        addError("wish", "wishField", "placeholder");
      }
    }

    // Wish Page: Confirm Button
    if (eventData.wish?.confirmButton?.enabled?.value) {
      if (!eventData.wish.confirmButton.text?.value) {
        addError("wish", "confirmButton", "text");
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [eventData]);

  const updateEventData = useCallback((section, data) => {
    setEventData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...data,
      },
    }));
    setIsDirty(true);
  }, []);

  const updateNestedEventData = useCallback(
    (section, subsection, data) => {
      setEventData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section][subsection],
            ...data,
          },
        },
      }));

      validateRequiredFields();

      setIsDirty(true);
    },
    [validateRequiredFields]
  );

  // Clear validation errors when required fields are filled
  useEffect(() => {
    if (Object.keys(validationErrors).length === 0) return;

    const newErrors = { ...validationErrors };
    let hasChanges = false;

    const clearFieldErrors = (section, sectionName) => {
      Object.entries(section).forEach(([key, field]) => {
        if (field && typeof field === "object") {
          if (
            field.hasOwnProperty("value") &&
            field.hasOwnProperty("required")
          ) {
            if (
              field.required &&
              field.value &&
              field.value !== "" &&
              newErrors[sectionName]?.[key]
            ) {
              delete newErrors[sectionName][key];
              hasChanges = true;
            }
          } else {
            // Handle nested objects
            Object.entries(field).forEach(([nestedKey, nestedField]) => {
              if (
                nestedField &&
                typeof nestedField === "object" &&
                nestedField.hasOwnProperty("value") &&
                nestedField.hasOwnProperty("required")
              ) {
                if (
                  nestedField.required &&
                  nestedField.value &&
                  nestedField.value !== "" &&
                  newErrors[sectionName]?.[key]?.[nestedKey]
                ) {
                  delete newErrors[sectionName][key][nestedKey];
                  hasChanges = true;
                  // Clean up empty nested error objects
                  if (Object.keys(newErrors[sectionName][key]).length === 0) {
                    delete newErrors[sectionName][key];
                  }
                }
              }
            });
          }
        }
      });
      // Clean up empty section error objects
      if (
        newErrors[sectionName] &&
        Object.keys(newErrors[sectionName]).length === 0
      ) {
        delete newErrors[sectionName];
        hasChanges = true;
      }
    };

    Object.entries(eventData).forEach(([sectionName, section]) => {
      clearFieldErrors(section, sectionName);
    });

    if (hasChanges) {
      setValidationErrors(newErrors);
    }
  }, [eventData, validationErrors]);

  // Helper: recursively extract plain values from our {value, required} structure
  const extractValues = useCallback((node) => {
    if (node && typeof node === "object") {
      // Flatten normal field objects
      if (
        Object.prototype.hasOwnProperty.call(node, "value") &&
        Object.prototype.hasOwnProperty.call(node, "required")
      ) {
        if (node.persistRaw) {
          return { value: node.value, required: node.required };
        }
        return node.value;
      }
      // NEW: flatten legacy shape { value: "..." } (no required)
      if (
        Object.prototype.hasOwnProperty.call(node, "value") &&
        !Object.prototype.hasOwnProperty.call(node, "required") &&
        Object.keys(node).length === 1
      ) {
        return node.value;
      }
      const result = {};
      Object.entries(node).forEach(([k, v]) => {
        const extracted = extractValues(v);
        if (extracted !== undefined) result[k] = extracted;
      });
      return result;
    }
    return node;
  }, []);

  // Helper: recursively rebuild structure from template (initialEventData) + firestore values
  const rebuildFromValues = useCallback((templateNode, dataNode, path = []) => {
    if (templateNode && typeof templateNode === "object") {
      if (
        Object.prototype.hasOwnProperty.call(templateNode, "value") &&
        Object.prototype.hasOwnProperty.call(templateNode, "required")
      ) {
        let valueSource = dataNode;
        // If template no longer uses persistRaw but data was stored as {value: ..., required: ...}
        if (
          !templateNode.persistRaw &&
          valueSource &&
          typeof valueSource === "object" &&
          valueSource.hasOwnProperty("value") &&
          Object.keys(valueSource).length <= 2
        ) {
          valueSource = valueSource.value; // flatten legacy stored object
        }
        if (
          templateNode.persistRaw &&
          valueSource &&
          typeof valueSource === "object" &&
          valueSource.hasOwnProperty("value")
        ) {
          valueSource = valueSource.value; // still flatten to internal value
        }
        let value =
          valueSource !== undefined ? valueSource : templateNode.value;
        const fieldKey = path[path.length - 1];
        if (fieldKey === "eventDate" && value) {
          if (value && typeof value === "object" && value.seconds) {
            value = new Date(value.seconds * 1000);
          } else if (typeof value === "number") {
            value = new Date(value > 10000000000 ? value : value * 1000);
          }
        }
        return { ...templateNode, value };
      }
      const result = Array.isArray(templateNode) ? [] : {};
      Object.entries(templateNode).forEach(([k, v]) => {
        result[k] = rebuildFromValues(v, dataNode ? dataNode[k] : undefined, [
          ...path,
          k,
        ]);
      });
      return result;
    }
    return templateNode;
  }, []);

  // Helper: deep clone preserving File objects
  const deepClone = useCallback((obj) => {
    if (obj === null || typeof obj !== "object") return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    // Preserve File / Blob references (do not JSON clone)
    if (typeof File !== "undefined" && obj instanceof File) return obj;
    if (typeof Blob !== "undefined" && obj instanceof Blob) return obj;
    if (Array.isArray(obj)) return obj.map((v) => deepClone(v));
    const cloned = {};
    Object.keys(obj).forEach((k) => {
      cloned[k] = deepClone(obj[k]);
    });
    return cloned;
  }, []);

  // Stage image instead of uploading immediately
  const uploadImageAndUpdateData = useCallback(
    async (section, field, file /*, eventId = null */) => {
      try {
        const validation = validateImageFile(file);
        if (!validation.success) {
          return { success: false, error: validation.error };
        }

        // Convert file to data URL for immediate preview
        const toDataURL = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

        const dataUrl = await toDataURL(file);
        // Mark field as pendingUpload so we know to upload on save
        updateNestedEventData(section, field, { value: dataUrl, pendingUpload: true });
        return { success: true, staged: true, preview: dataUrl };
      } catch (error) {
        console.error("Error staging image:", error);
        return { success: false, error: error.message };
      }
    },
    [updateNestedEventData]
  );

  // Convert base64 data URL to File (for upload)
  const dataURLToFile = useCallback((dataUrl, fileName) => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    try {
      return new File([u8arr], fileName, { type: mime });
    } catch {
      return new Blob([u8arr], { type: mime });
    }
  }, []);

  // Upload any staged (pendingUpload) images and return a new eventData object with updated URLs
  const processPendingImageUploads = useCallback(
    async (docId) => {
      const workingData = deepClone(eventData);
      const uploadPromises = [];

      const handleField = (sectionName, keyChain, fieldObj) => {
        if (
          fieldObj &&
          typeof fieldObj === "object" &&
          Object.prototype.hasOwnProperty.call(fieldObj, "value") &&
          Object.prototype.hasOwnProperty.call(fieldObj, "required")
        ) {
          const needsUpload = fieldObj.pendingUpload && fieldObj.value;
          const isAlreadyUploaded = isFirebaseStorageUrl(fieldObj.value);
          if (needsUpload && !isAlreadyUploaded) {
            uploadPromises.push(
              (async () => {
                let uploadFile = fieldObj.value;
                if (typeof uploadFile === "string" && isBase64DataUrl(uploadFile)) {
                  uploadFile = dataURLToFile(uploadFile, `${keyChain.join("_")}_${Date.now()}.jpg`);
                }
                // Skip if result not a File/Blob (defensive)
                if (
                  !(typeof File !== "undefined" && uploadFile instanceof File) &&
                  !(typeof Blob !== "undefined" && uploadFile instanceof Blob)
                ) {
                  return;
                }
                const folder = `events/${docId}/${keyChain[0]}`; // first key after section is the subsection/field grouping
                const fileName = `${keyChain.slice(1).join("_")}_${Date.now()}.jpg`;
                const downloadURL = await uploadImage(uploadFile, folder, fileName);

                // Apply change into workingData following keyChain
                let cursor = workingData;
                for (let i = 0; i < keyChain.length - 1; i++) {
                  cursor = cursor[keyChain[i]];
                }
                const leafKey = keyChain[keyChain.length - 1];
                cursor[leafKey] = {
                  ...cursor[leafKey],
                  value: downloadURL,
                  pendingUpload: false,
                };
              })()
            );
          }
        } else if (fieldObj && typeof fieldObj === "object") {
          Object.entries(fieldObj).forEach(([k, v]) =>
            handleField(sectionName, [...keyChain, k], v)
          );
        }
      };

      Object.entries(workingData).forEach(([sectionName, sectionObj]) => {
        Object.entries(sectionObj).forEach(([k, v]) =>
          handleField(sectionName, [sectionName, k], v)
        );
      });

      await Promise.all(uploadPromises);
      return workingData;
    },
    [deepClone, eventData, dataURLToFile]
  );

  const saveEventData = useCallback(
    async (eventId = null) => {
      try {
        const isValid = validateRequiredFields();
        if (!isValid) {
          return {
            success: false,
            message: "Please fill in all required fields before saving.",
            validationErrors,
          };
        }

        // Determine / create doc id early so we can upload staged images
        let docId = eventId;
        let isNew = false;
        if (!docId) {
          const tmpRef = doc(collection(db, "events")); // create ref without writing
          docId = tmpRef.id;
          isNew = true;
        }

        // Upload staged images
        const workingData = await processPendingImageUploads(docId);

        // Prepare data for Firestore
        const firestoreData = {};
        Object.entries(workingData).forEach(([sectionKey, section]) => {
          firestoreData[sectionKey] = extractValues(section);
        });
        firestoreData.updatedAt = new Date();
        if (isNew) firestoreData.createdAt = new Date();

        // Persist
        await setDoc(doc(db, "events", docId), firestoreData, { merge: true });

        // Update state with uploaded URLs
        setEventData(workingData);
        setIsDirty(false);
        if (isNew) setCurrentEventId(docId);

        return {
          success: true,
          message: eventId ? "Event updated successfully!" : "Event created successfully!",
          eventId: docId,
        };
      } catch (error) {
        console.error("Failed to save event data:", error);
        return {
          success: false,
          message: `Failed to save event data: ${error.message}`,
        };
      }
    },
    [
      extractValues,
      processPendingImageUploads,
      validateRequiredFields,
      validationErrors,
      setEventData,
      setCurrentEventId,
    ]
  );

  const loadEventData = useCallback(
    async (eventId) => {
      try {
        console.log("Loading event data for ID:", eventId);
        setIsLoading(true);
        const docRef = doc(db, "events", eventId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const firestoreData = docSnap.data();
          const loadedEventData = {};
          Object.entries(initialEventData).forEach(
            ([sectionKey, sectionTemplate]) => {
              loadedEventData[sectionKey] = rebuildFromValues(
                sectionTemplate,
                firestoreData[sectionKey] || {},
                [sectionKey]
              );
            }
          );
          setEventData(loadedEventData);
          setIsDirty(false);
          console.log("Event data loaded successfully:", loadedEventData);
          return { success: true };
        } else {
          console.warn("Event not found for ID:", eventId);
          return { success: false, message: "Event not found" };
        }
      } catch (error) {
        console.error("Failed to load event data:", error);
        return {
          success: false,
          message: `Failed to load event data: ${error.message}`,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [rebuildFromValues]
  );

  // Load data automatically when eventId changes
  useEffect(() => {
    if (currentEventId && !isLoading) {
      console.log("Auto-loading event data for ID:", currentEventId);
      loadEventData(currentEventId).catch((error) => {
        console.error("Auto-load failed:", error);
      });
    }
  }, [currentEventId, loadEventData]);

  const setEventId = useCallback((eventId) => {
    setCurrentEventId(eventId);
  }, []);

  const deleteEvent = useCallback(async (eventId) => {
    try {
      await deleteDoc(doc(db, "events", eventId));
      return { success: true };
    } catch (error) {
      console.error("Failed to delete event:", error);
      return { success: false, error: error.message };
    }
  }, []);

  const value = {
    eventData,
    updateEventData,
    updateNestedEventData,
    uploadImageAndUpdateData, // now stages images
    saveEventData,
    loadEventData,
    deleteEvent, // Added deleteEvent
    setEventId,
    currentEventId,
    isLoading,
    isDirty,
    validateRequiredFields,
    validationErrors,
  };

  return (
    <EventDataContext.Provider value={value}>
      {children}
    </EventDataContext.Provider>
  );
}
